import { stores } from '@kurtosys/app-start';
import { common } from '@kurtosys/ksys-app-template';
import { TEntityType } from '@kurtosys/types/api/common/TEntityType.js';
import { TFilterProperties } from '@kurtosys/types/app/filters/TFilterProperties.js';

import { IComponentStyles } from '../../../../models/app/IComponentStyles.js';
import { IConfiguration } from '../../../../models/app/IConfiguration.js';
import { AppStore } from '../AppStore.js';

interface IFilterReference {
	key?: string;
	entityType: TEntityType;
	property: string;
}

interface IPropertyMappingConfigValue {
	value: string;
	childProperty?: IFilterReference;
	disabled?: boolean;
}

interface IPropertyMappingConfig {
	disabled?: boolean;
	entityType: TEntityType;
	property: string;
	key?: string; // used to split out values for the same property into different sub-lists
	values: IPropertyMappingConfigValue[];
}

interface IPropertyMapping {
	entityType: TEntityType;
	property: string;
	values: Record<string, IFilterReference | null>;
}

export interface IFilterParentReference {
	entityType: TEntityType;
	property: string;
	value: string;
	key?: string;
}

type TPropertyMappings = Record<string, IPropertyMapping>;
type TReverseMappings = Record<string, IFilterParentReference[]>;

export class PropertyMappingStore extends stores.base.ClientConfigurationStoreBase<
	IPropertyMappingConfig[],
	IConfiguration,
	IComponentStyles
> {
	protected code = 'property_mapping';
	protected key = 'mappings';

	protected mapping: TPropertyMappings = {};
	protected reverseMapping: TReverseMappings = {};

	protected orchestrate(propertyMappingConfigs: IPropertyMappingConfig[]) {
		if (propertyMappingConfigs) {
			for (const propertyMappingConfig of propertyMappingConfigs) {
				if (!propertyMappingConfig.disabled) {
					const { entityType, property, key, values } = propertyMappingConfig;
					if (entityType && property && common.commonUtils.isArrayLike(values)) {
						const mappingKey = this.getMappingKey(entityType, property, key);
						if (this.mapping[mappingKey] !== undefined) {
							console.warn(
								`Client Configuration Filter Mapping: Duplicate filter property ${property} found for entity type ${entityType}. Please review filter mapping configuration`,
							);
						} else {
							this.mapping[mappingKey] = {
								entityType,
								property,
								values: values.reduce((filteredValues: IPropertyMapping['values'], meta) => {
									if (!meta.disabled && meta.value) {
										let value: IFilterReference | null = null;
										if (
											meta.childProperty &&
											meta.childProperty.entityType &&
											meta.childProperty.property
										) {
											value = meta.childProperty;
											// Add value to reverse mapping
											const reverseMappingKey = this.getMappingKey(meta.childProperty.entityType, meta.childProperty.property, meta.childProperty.key);
											if (!this.reverseMapping[reverseMappingKey]) {
												this.reverseMapping[reverseMappingKey] = [];
											}
											this.reverseMapping[reverseMappingKey].push({
												entityType,
												property,
												key: value.key,
												value: meta.value,
											});
										}
										filteredValues[meta.value] = value;
									}
									return filteredValues;
								}, {} as IPropertyMapping['values']),
							};
						}
					}
				}
			}
		}
	}

	private getMappingKey(entityType: TEntityType, property: string, key?: string) {
		return key ? `${entityType}-${property}-${key}` : `${entityType}-${property}`;
	}

	/**
	 * This flag should be driven by configuration.
	 *
	 * @readonly
	 */
	public get enabled(): boolean {
		const appStore = this.storeContext.get<AppStore>('appStore');
		return appStore?.appComponentConfiguration?.features?.useFilterPropertyMapping || false;
	}

	/**
	 * Filters the filter values based on what the filter mapping allows.
	 *
	 * Note: This is based on the value's disabled field within the filter mapping,
	 * which has been accounted for within the orchestrateValues method.
	 *
	 * @param entityType
	 * @param property
	 * @param values
	 * @returns
	 */
	public getAllowedFilterValues(entityType: TEntityType, property: string, values: string[], key?: string): string[] {
		const mappingKey = this.getMappingKey(entityType, property,key);
		if (this.mapping[mappingKey]) {
			const valueKeys = Object.keys(this.mapping[mappingKey].values);
			return values.filter((value) => valueKeys.includes(value));
		}
		return values;
	}

	/**
	 * Returns the child filter's entityType & property, which can be used in the filter property store to obtain filter values.
	 *
	 * @param entityType
	 * @param property
	 * @param valueKey
	 * @returns
	 */
	public getFilterChildReference(entityType: TEntityType, property: string, valueKey: string, key?: string): IFilterReference {
		const mappingKey = this.getMappingKey(entityType, property, key);
		if (this.mapping[mappingKey]) {
			const { values } = this.mapping[mappingKey];
			return values[valueKey];
		}
	}

	/**
	 * Returns the child filter's entityType & property, which can be used in the filter property store to obtain filter values.
	 *
	 * @param entityType
	 * @param property
	 * @param valueKey
	 * @returns
	 */
	private getNestedReferences(entityType: TEntityType, property: string, key?: string): Record<string, IFilterReference> {
		const mappingKey = this.getMappingKey(entityType, property, key);
		if (this.mapping[mappingKey]) {
			const { values } = this.mapping[mappingKey];
			return values;
		}
	}

	/**
	 * Returns the parent filter's entityType & property (1 level up), which can be used in the filter property store to obtain filter values.
	 *
	 * @param entityType
	 * @param property
	 * @param valueKey
	 * @returns
	 */
	public getParentReferences(entityType: TEntityType, property: string, filterValue: string[] = []): IFilterParentReference[] {
		const mappingKey = this.getMappingKey(entityType, property);
		const reverseMappingKeys = Object.keys(this.reverseMapping).filter(k => k.startsWith(mappingKey));
		let parents = reverseMappingKeys.reduce((arr: IFilterParentReference[], key: string) => {
			arr.push(...this.reverseMapping[key]);			
			return arr;
		}, []);
		if (filterValue.length > 0) {
			parents = parents.filter(p => {
				const allowedValues = this.getAllowedFilterValues(entityType, property, filterValue, p.key);
				return allowedValues.length > 0;
			});
		}
		return parents;
	}

	/**
	 * Returns the filter properties object with child filter properties added
	 *
	 * @param filterProperties
	 * @returns
	 */
	public applyNestedProperties(filterProperties: TFilterProperties): TFilterProperties {
		const entityTypes = Object.keys(filterProperties) as TEntityType[];
		for (const entityType of entityTypes) {
			const properties = Object.keys(filterProperties[entityType]);
			for (const property of properties) {
				const nestedReferences = this.getNestedReferences(entityType, property);
				if (nestedReferences) {
					const nestedKeys = Object.keys(nestedReferences);
					for (const nestedKey of nestedKeys) {
						if (nestedReferences[nestedKey]) {
							const { entityType: childEntityType, property: childProperty } =
								nestedReferences[nestedKey];
							if (!filterProperties[childEntityType]) {
								filterProperties[childEntityType] = {};
							}
							if (!filterProperties[childEntityType][childProperty]) {
								filterProperties[childEntityType][childProperty] = {
									entityType: childEntityType,
									property: childProperty,
									counts: false,
								};
							}
						}
					}
				}
			}
		}
		return filterProperties;
	}
}
